<template>
  <b-container
    v-if="assetModule"
    fluid
    class="base-panel"
    id="print-sensor-data"
    :key="assetModule.id"
  >
    <b-row>
      <engineering-modules-header
        class="engineering__header"
        :show-info-icon="showInfoIcon"
        show-treeview-icon
        :selected-id="asset.id"
        :lastUpdate="lastUpdate"
        :show-update-icon="!hideUpdateButton"
        :show-print-icon="!hidePrintButton"
        :company="currentInstallation.companyName"
        :installation="currentInstallation.name"
        :regional="currentInstallation.regionalName"
        :asset="asset.name"
        :title="assetModule.name"
        :image-url="assetModule.imageUrl"
        :notifications="notifications"
      />
    </b-row>
    <b-row class="mt-3 px-3">
      <b-col
        class="p-0"
        cols="12"
      >
        <b-tabs v-if="isTabsVisible">
          <b-tab
            v-for="tab in tabs"
            lazy
            :key="tab.name"
            :disabled="isTabDisabled(tab)"
            :active="isTabActive(tab)"
            @click="changeRouteTab(tab)"
          >
            <template #title>
              {{ tab.title }}
              <info-badge
                v-show="tab.showTooltip"
                class="base-panel__tab-disabled-info ml-1"
                boundary="window"
                placement="top"
                :translation-key="tab.tooltipMessage"
              />
            </template>
            <b-container
              class="mx-0"
              fluid
            >
              <b-row no-gutters>
                <b-col cols="12">
                  <component
                    :is="tab.component"
                    :asset="asset"
                    :asset-type="assetType"
                    :asset-module="assetModule"
                    :asset-general-info="assetGeneralInfo"
                    :tab-info="tab"
                    @cant-leave-changed="cantLeaveTabChanged"
                    @change-pending-samples="assignAmountOfPendingSamples"
                    @change-pending-lots="assignAmountOfPendingLots"
                    @change-pagination="onChangePagination"
                  />
                </b-col>
              </b-row>
            </b-container>
          </b-tab>
        </b-tabs>
        <slot v-else/>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import eventBus from '@/services/globalEventBus';
import engineeringEvents from '@/domain/events/engineering';
import engineeringModulesHeaderEvent from '@/domain/events/engineeringModulesHeader';
import LoadingMixin from '@/services/loadingMixin';
import InfoBadge from '@/components/icon/InfoBadge';
import EngineeringModulesHeader from '@/components/header/EngineeringModulesHeader';

export default {
  name: 'EngineeringModule',
  props: {
    asset: {
      type: Object,
    },
    assetType: {
      type: Object,
      required: false,
    },
    assetModule: {
      type: Object,
    },
    assetGeneralInfo: {
      type: Object,
    },
    tabs: {
      type: Array,
      required: false,
    },
    hidePrintButton: {
      type: Boolean,
      default: true,
    },
    hideUpdateButton: {
      type: Boolean,
      default: false,
    },
    lastUpdate: {
      type: String,
    },
    hideTabs: {
      type: Boolean,
      default: false,
    },
    notifications: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    InfoBadge,
    EngineeringModulesHeader,
  },
  mixins: [
    LoadingMixin,
  ],
  data: () => ({
    cantLeaveTab: false,
    commonParams: {},
  }),
  computed: {
    ...mapGetters([
      'installation',
    ]),
    currentInstallation() {
      return this.installation || {};
    },
    isTabsVisible() {
      return this.tabs && (this.tabs || []).length && !this.hideTabs;
    },
    activeTabIndex() {
      return this.tabs.findIndex(({ name }) => name === this.routeTabName);
    },
    activeTab() {
      return this.tabs[this.activeTabIndex];
    },
    routeTabName() {
      const routeTabName = this.$route.query && this.$route.query.tab;
      if (routeTabName && (this.tabs || []).some(({ name }) => name === routeTabName)) {
        return routeTabName;
      }

      const [activeTab] = this.tabs || [];
      return activeTab?.name || '';
    },
    showInfoIcon() {
      return this.asset.features?.length > 0;
    },
  },
  created() {
    eventBus.on(engineeringModulesHeaderEvent.refresh, this.refreshData, this);
  },
  beforeDestroy() {
    eventBus.off(engineeringModulesHeaderEvent.refresh, this.refreshData, this);
  },
  methods: {
    onChangePagination(currentPage) {
      this.$set(this.commonParams, 'sampleListPage', currentPage);
    },
    isTabDisabled(tab) {
      if (this.routeTabName === tab.name) {
        return false;
      }
      return tab.disabled || this.cantLeaveTab;
    },
    cantLeaveTabChanged(cantLeaveTab) {
      this.cantLeaveTab = cantLeaveTab;
    },
    isTabActive({ name }) {
      return name === this.routeTabName;
    },
    changeRouteTab({ name }) {
      if (this.isTabActive({ name })) {
        return;
      }

      this.$router.push({
        name: this.$route.name,
        params: this.commonParams,
        query: {
          tab: name,
        },
      });
    },
    refreshData({ wait }) {
      eventBus.emit(engineeringEvents.refreshData, { wait });
    },
    assignAmountOfPendingSamples(amount) {
      this.$emit('change-pending-samples', amount);
    },
    assignAmountOfPendingLots(amount) {
      this.$emit('change-pending-lots', amount);
    },
  },
  watch: {
    routeTabName() {
      this.cantLeaveTab = false;
    },
  },
};
</script>

<style lang="sass" scoped>
  .base-panel
    padding: 2rem
    &__tab-disabled-info
      pointer-events: all
  ::v-deep
    .nav-tabs > .nav-item
      .disabled
        background: var(--color-table-header-bg)
    .nav-item > .nav-link
      @media print
        text-decoration: none
</style>
